/* eslint-env browser */
import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import {
    Box,
    Button,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Switch,
    FormControlLabel,
    Paper,
    Skeleton,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { api } from 'common';
import DashboardCard, { RAISED_SURFACE_SX } from '../components/DashboardCard';
import { useDemandSnapshot, decodeDemandSnapshot, DemandHeatLayer, DemandZonesCard, zonePinIcon } from '../components/DemandHeatOverlay';
import { showToast, InlineMessage, EmptyState } from '../components/feedback';
import { tokens as c, FONT_STACK } from '../theme';
import { ADDIS_OFFSET_MS } from '../common/exportUtils';
import { getBookingType, bookingTypeMeta, bookingTypeLabelFor, BOOKING_TYPE_ORDER } from 'common/sharedFunctions';

// The charts load on their own, after the page, so ApexCharts is not part of
// the first download of every portal page (M13).
const Chart = lazy(() => import('react-apexcharts'));
const FleetHealthCharts = lazy(() => import('../components/FleetHealthCharts'));

// ---------------------------------------------------------------------------
// Map (Q16): the live map opens on Addis Ababa straight away. It never waits
// for the staff browser's location (a shared desk PC often has none, or it
// was blocked); "Centre on me" asks for it only when clicked.
// ---------------------------------------------------------------------------
const DEFAULT_CENTER = { lat: 9.03, lng: 38.74 };
const DEFAULT_ZOOM = 12;
const MY_LOCATION_ZOOM = 14;
const MAP_HEIGHT = 'clamp(320px, 55vh, 720px)';
const MAP_MIN_HEIGHT = 320;   // beside the tiles (lg) the map fills the row, never shorter than this
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const MAPS_POLL_MS = 500;
const MAPS_POLL_MAX = 60; // give the Maps script 30 s before saying it failed
const FALLBACK_CAR_ICON = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
const MAP_OPTIONS = {
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: true,
    fullscreenControl: true,
    minZoom: 10,
    maxZoom: 18,
    styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    ],
};

// The Maps JS API is loaded once by App.js (useJsApiLoader). The bootstrap
// defines window.google.maps before the core classes exist, so wait for
// google.maps.Map before mounting <GoogleMap> (it throws without it).
const mapsLoaded = () => Boolean(window.google && window.google.maps && window.google.maps.Map);
function useMapsReady() {
    const [state, setState] = useState(() => (mapsLoaded() ? 'ready' : 'waiting'));
    useEffect(() => {
        if (state !== 'waiting') return undefined;
        let tries = 0;
        const id = setInterval(() => {
            tries += 1;
            if (mapsLoaded()) {
                setState('ready');
                clearInterval(id);
            } else if (tries >= MAPS_POLL_MAX) {
                setState('failed');
                clearInterval(id);
            }
        }, MAPS_POLL_MS);
        return () => clearInterval(id);
    }, [state]);
    return state;
}

// ---------------------------------------------------------------------------
// Chart colours (M13): FleetHealthCharts' validated palette, so every chart on
// the Dashboard speaks one colour language. The series orders below were
// checked with the dataviz validator on white: no neighbouring pair falls
// under the colour-blind or normal-vision floors. Yellow is under 3:1 on white,
// so every chart carries a legend with each series' total, and a tooltip.
// ---------------------------------------------------------------------------
const VIZ = {
    blue: '#2a78d6',
    orange: '#eb6834',
    aqua: '#1baf7a',
    yellow: '#eda100',
    magenta: '#e87ba4',
    green: '#008300',
    violet: '#6250d6',
    red: '#e34948',
    fold: '#898781', // agents past the coloured slots: grey, named by the legend and tooltip
    grid: '#e1e0d9',
};
// Booking chart, in this order: completed, payment pending, cancelled,
// shared trip, fixed price (the customer set the fare: offerFare > 0).
const BOOKING_SERIES = [
    { key: 'COMPLETE', color: VIZ.green },
    { key: 'PAYMENT_PENDING', color: VIZ.yellow },
    { key: 'CANCELLED', color: VIZ.red },
    { key: 'SHARED_TRIP', color: VIZ.blue },
    { key: 'PRICE_OVERRIDE', color: VIZ.orange },
];
// Onboarding chart: the fixed lines, then the agents someone picks. Issue
// lines are dashed as well as coloured.
const ONBOARDING_COLORS = { total: VIZ.blue, no_car: VIZ.red, no_licence: VIZ.yellow, no_tin: VIZ.violet, no_agent: VIZ.aqua };
const ISSUE_SERIES = ['no_car', 'no_licence', 'no_tin'];
const AGENT_COLORS = [VIZ.green, VIZ.magenta];
const CHART_HEIGHT = 340;

// ---------------------------------------------------------------------------
// Addis Ababa calendar maths (UTC+3, no daylight saving): every "today", day,
// week and month on the Dashboard is the Addis one, whatever time zone the
// staff browser is set to.
// ---------------------------------------------------------------------------
const DAY_MS = 86400000;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toMs = (v) => {
    if (v === null || v === undefined || v === '') return NaN;
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : NaN;
};
const addisDayNo = (ms) => Math.floor((ms + ADDIS_OFFSET_MS) / DAY_MS);
const addisMonthNo = (ms) => {
    const d = new Date(ms + ADDIS_OFFSET_MS);
    return d.getUTCFullYear() * 12 + d.getUTCMonth();
};
const sundayWeekNo = (dayNo) => Math.floor((dayNo + 4) / 7); // 1 Jan 1970 was a Thursday
const dayParts = (dayNo) => {
    const d = new Date(dayNo * DAY_MS);
    return { day: d.getUTCDate(), month: d.getUTCMonth(), year: d.getUTCFullYear() };
};
const dayLabel = (dayNo) => {
    const p = dayParts(dayNo);
    return `${p.day} ${MONTH_NAMES[p.month]}`;
};
const monthLabel = (monthNo) => `${MONTH_NAMES[monthNo % 12]} ${Math.floor(monthNo / 12)}`;

// Booking chart buckets: the last 30 Addis days, the last 12 weeks (Sunday to
// Saturday) or the last 12 months. index(ms) is 0..n-1, or -1 outside.
// `labels` go on the axis, `fullLabels` in the tooltip.
const bookingBuckets = (period, nowMs) => {
    const today = addisDayNo(nowMs);
    if (period === 'week') {
        const first = sundayWeekNo(today) - 11;
        const starts = Array.from({ length: 12 }, (_, i) => (first + i) * 7 - 4);
        return {
            labels: starts.map(dayLabel),
            fullLabels: starts.map((d) => `${dayLabel(d)} – ${dayLabel(d + 6)}`),
            index: (ms) => {
                const i = sundayWeekNo(addisDayNo(ms)) - first;
                return i >= 0 && i < 12 ? i : -1;
            },
        };
    }
    if (period === 'month') {
        const first = addisMonthNo(nowMs) - 11;
        const months = Array.from({ length: 12 }, (_, i) => first + i);
        return {
            labels: months.map((m) => MONTH_NAMES[m % 12]),
            fullLabels: months.map(monthLabel),
            index: (ms) => {
                const i = addisMonthNo(ms) - first;
                return i >= 0 && i < 12 ? i : -1;
            },
        };
    }
    const first = today - 29;
    const days = Array.from({ length: 30 }, (_, i) => first + i);
    return {
        labels: days.map((d) => String(dayParts(d).day)),
        fullLabels: days.map(dayLabel),
        index: (ms) => {
            const i = addisDayNo(ms) - first;
            return i >= 0 && i < 30 ? i : -1;
        },
    };
};

// Onboarding chart buckets, as before: the last 30 Addis days, four rolling
// 7-day windows that end today, or the months of this Addis year.
const onboardingBuckets = (period, nowMs) => {
    if (period === 'week') {
        const today = addisDayNo(nowMs);
        const starts = Array.from({ length: 4 }, (_, i) => today - 7 * (3 - i) - 6);
        return {
            labels: starts.map(dayLabel),
            fullLabels: starts.map((d) => `${dayLabel(d)} – ${dayLabel(d + 6)}`),
            index: (ms) => {
                const diff = today - addisDayNo(ms);
                if (!(diff >= 0)) return -1;
                const w = Math.floor(diff / 7);
                return w < 4 ? 3 - w : -1;
            },
        };
    }
    if (period === 'month') {
        const year = Math.floor(addisMonthNo(nowMs) / 12);
        return {
            labels: MONTH_NAMES.slice(),
            fullLabels: MONTH_NAMES.map((m) => `${m} ${year}`),
            index: (ms) => {
                const m = addisMonthNo(ms);
                return Math.floor(m / 12) === year ? m % 12 : -1;
            },
        };
    }
    return bookingBuckets('day', nowMs);
};

const sumOf = (arr) => (arr || []).reduce((s, v) => s + (Number(v) || 0), 0);
// Whole-number y axis: with a small maximum, one tick per unit (so 0.5 never
// shows up as a second "1"); otherwise five nice steps.
const yTickAmount = (seriesList) => {
    let max = 0;
    seriesList.forEach((s) => (s.data || []).forEach((v) => { if (v > max) max = v; }));
    return max > 0 && max < 5 ? max : 5;
};
const fmtCount = (n) => {
    const v = Number(n);
    return (Number.isFinite(v) ? Math.round(v) : 0).toLocaleString('en-US');
};
const isIssueSeries = (s) => ISSUE_SERIES.indexOf(s.id) >= 0;
const axisText = { fontFamily: FONT_STACK, colors: c.TEXT_3, fontSize: '12px' };
// On a phone, 30 day numbers do not fit under the chart: keep every third one
// (always today's); the tooltip still names each day in full.
const axisCategories = (labels, sparse) => {
    if (!sparse || labels.length <= 12) return labels;
    const last = labels.length - 1;
    return labels.map((label, i) => ((last - i) % 3 === 0 ? label : ''));
};
const chartBase = (type) => ({
    type,
    fontFamily: FONT_STACK,
    foreColor: c.TEXT_3,
    animations: { enabled: false },
});
const chartLegend = (totals) => ({
    show: true,
    position: 'bottom',
    horizontalAlign: 'left',
    fontFamily: FONT_STACK,
    fontSize: '13px',
    labels: { colors: c.INK_2 },
    markers: { width: 10, height: 10, radius: 3 },
    itemMargin: { horizontal: 10, vertical: 4 },
    // The series total beside each name: a visible value for every colour.
    formatter: (name, opts) => `${name} · ${fmtCount(totals[opts.seriesIndex])}`,
});

const Dashboard = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const complaindata = useSelector(state => state.complaindata);
    const sosdata = useSelector(state => state.sosdata);
    const dispatch = useDispatch();
    const { clearFetchDrivers, subscribeActiveDriversLive, fetchCustomerStats } = api;

    const settings = settingsdata.settings || {};
    const profile = auth.profile || {};
    // The signed-in role, read straight from the profile (no state, so the
    // tiles do not pop in a render later).
    const role = profile.usertype || null;
    const [locations, setLocations] = useState([]);
    const [selectedMarkerId, setSelectedMarkerId] = useState(null);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [bookingsTimePeriod, setBookingsTimePeriod] = useState('day'); // 'day', 'week', 'month'
    // Booking-source filter for the booking chart (call center / street pickup /
    // app / donkey meter / offline / admin). Defaults to all types selected so
    // the chart is unchanged until the user narrows it.
    const [selectedBookingTypes, setSelectedBookingTypes] = useState(BOOKING_TYPE_ORDER);
    const [timePeriod, setTimePeriod] = useState('day'); // onboarding chart: 'day', 'week', 'month'
    // Agents compared on the onboarding chart (ids 'agent:<uid>'). Each keeps
    // its colour while it stays picked; a new pick takes a free colour, or grey.
    const [selectedAgents, setSelectedAgents] = useState([]);
    const agentColorRef = useRef({});

    // Map state
    const mapsState = useMapsReady();
    const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
    const mapRef = useRef(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const zoomTimeoutRef = useRef(null);
    const [locating, setLocating] = useState(false);
    const [locationNote, setLocationNote] = useState('');
    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => { aliveRef.current = false; };
    }, []);

    // Call-center + on-demand mode: customers aren't loaded into usersdata.users,
    // so the customer count / invitation tiles come from the cheap
    // /customer_index_stats counter node instead of counting the array.
    const usePaginatedCustomers = settings.useCustomerOnDemand === true
        && ['callcenteragent', 'callcentersupervisor', 'scout'].includes(profile.usertype);
    const customerStats = usersdata.customers ? usersdata.customers.stats : null;
    useEffect(() => {
        if (usePaginatedCustomers) dispatch(fetchCustomerStats());
    }, [usePaginatedCustomers, dispatch, fetchCustomerStats]);

    // Fleet & app health: the nightly /dashboard_stats snapshot. Read once
    // per dashboard mount (it changes once a day), kept in component state.
    const [fleetStats, setFleetStats] = useState(null);
    const [fleetStatsLoading, setFleetStatsLoading] = useState(false);
    const [fleetStatsError, setFleetStatsError] = useState(null);
    const [fleetStatsBuilding, setFleetStatsBuilding] = useState(false);
    const fleetRoles = ['admin', 'finance', 'callcentersupervisor', 'callcenteragent'];
    const showFleetHealth = fleetRoles.includes(role);

    // Gross Earnings cards: settings.dashboardGrossEarnings applies to every
    // role alike — 'all' shows them, 'none' hides them (admins too); unset or
    // 'admin' keeps the original admin-only behaviour.
    const grossMode = settings.dashboardGrossEarnings || 'admin';
    const showGross = grossMode === 'all' || (grossMode === 'admin' && role === 'admin');
    useEffect(() => {
        if (!showFleetHealth) return undefined;
        let alive = true;
        setFleetStatsLoading(true);
        setFleetStatsError(null);
        api.fetchDashboardStats()
            .then((snap) => { if (alive) setFleetStats(snap); })
            .catch((e) => { if (alive) setFleetStatsError((e && e.code) || 'read_failed'); console.log('[Dashboard] fleet stats unavailable:', e && e.message); })
            .finally(() => { if (alive) setFleetStatsLoading(false); });
        return () => { alive = false; };
    }, [showFleetHealth]);

    // Driver demand heat map ("Hot zones") on this SAME map instance — the
    // snapshot drivers see, painted as a canvas overlay. Gated on the
    // step-2 flag (settings.driverDemandMapEnabled) and the roles allowed to
    // read /demand_heat by the rules. The switch is remembered per browser.
    // Data = one-shot reads every 10 min while the tab is visible
    // (components/DemandHeatOverlay.js): no listener and no second map, so
    // neither the RTDB nor the Maps bill moves. It waits for the map to be
    // ready, never for the staff member's location (Q16).
    const demandRoles = ['admin', 'fleetadmin', 'finance', 'callcentersupervisor', 'callcenteragent'];
    const demandAvailable = settings.driverDemandMapEnabled === true && demandRoles.includes(role);
    const [demandOn, setDemandOn] = useState(() => {
        try { return window.localStorage.getItem('dd_dashboard_demand') === '1'; } catch (e) { return false; }
    });
    const [demandTab, setDemandTab] = useState('n');
    const toggleDemand = useCallback((on) => {
        setDemandOn(on);
        try { window.localStorage.setItem('dd_dashboard_demand', on ? '1' : '0'); } catch (e) { /* private mode: not persisted */ }
    }, []);
    const showDemand = demandAvailable && demandOn && mapLoaded;
    const demand = useDemandSnapshot({ enabled: showDemand, withMeta: role === 'admin' });
    const demandView = useMemo(() => decodeDemandSnapshot(demand.snap, demandTab), [demand.snap, demandTab]);
    // Admin "Build snapshot now": the server runs the nightly rebuilds once
    // (throttled to one manual build per 6 hours) and answers with the data.
    const buildFleetStats = async () => {
        if (fleetStatsBuilding) return;
        setFleetStatsBuilding(true);
        try {
            const res = await api.buildDashboardStats();
            setFleetStats({ drivers: res.drivers || null, customers: res.customers || null });
            setFleetStatsError(null);
        } catch (e) {
            const msg = e && e.code === 'too_soon'
                ? t('fleet_health_too_soon', { defaultValue: 'A manual build already ran in the last 6 hours. The nightly job will refresh it.' })
                : ((e && e.message) || t('dash_build_failed', { defaultValue: 'The snapshot could not be built. Try again later.' }));
            showToast.error(msg);
        } finally {
            setFleetStatsBuilding(false);
        }
    };

    // Marker size follows the zoom level.
    const currentIconSize = useMemo(() => {
        if (mapZoom >= 15) return { width: 45, height: 35 };
        if (mapZoom >= 13) return { width: 35, height: 25 };
        return { width: 25, height: 18 };
    }, [mapZoom]);

    // Debounced zoom handler
    const handleZoomChange = useCallback(() => {
        if (mapRef.current && mapLoaded) {
            if (zoomTimeoutRef.current) {
                clearTimeout(zoomTimeoutRef.current);
            }
            zoomTimeoutRef.current = setTimeout(() => {
                const newZoom = mapRef.current ? mapRef.current.getZoom() : null;
                if (newZoom && newZoom !== mapZoom) {
                    setMapZoom(newZoom);
                }
            }, 200);
        }
    }, [mapLoaded, mapZoom]);

    const handleMapLoad = useCallback((map) => {
        mapRef.current = map;
        setMapLoaded(true);
    }, []);
    const handleMapUnmount = useCallback(() => {
        mapRef.current = null;
        setMapLoaded(false);
    }, []);

    const resetView = () => {
        if (!mapRef.current) return;
        mapRef.current.panTo(DEFAULT_CENTER);
        mapRef.current.setZoom(DEFAULT_ZOOM);
    };

    // "Centre on me": the browser's location is asked for only on this click.
    // If it is off, blocked or missing, one line says so and the map stays put.
    const centreOnMe = () => {
        const unavailable = t('dash_location_unavailable', { defaultValue: 'Your location is not available in this browser, so the map stays on Addis Ababa.' });
        setLocationNote('');
        if (!navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== 'function') {
            setLocationNote(unavailable);
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (!aliveRef.current) return;
                setLocating(false);
                const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
                if (mapRef.current) {
                    mapRef.current.panTo(loc);
                    mapRef.current.setZoom(MY_LOCATION_ZOOM);
                }
            },
            () => {
                if (!aliveRef.current) return;
                setLocating(false);
                setLocationNote(unavailable);
            },
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
        );
    };

    useEffect(() => {
        // LIVE feed: auto-updating slim markers filtered by real-time eligibility
        // (drivers drop off the map the instant they go offline/busy, not after
        // the 2-min slim rebuild). Returns an unsubscribe fn.
        const unsubscribe = dispatch(subscribeActiveDriversLive());
        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
            dispatch(clearFetchDrivers());
            // Clean up zoom timeout on unmount
            if (zoomTimeoutRef.current) {
                clearTimeout(zoomTimeoutRef.current);
            }
        };
    }, [dispatch, subscribeActiveDriversLive, clearFetchDrivers]);

    // Keep the last bookings on screen while a refetch is in flight.
    useEffect(() => {
        if (bookinglistdata.bookings) {
            setFilteredBookings(bookinglistdata.bookings);
        }
    }, [bookinglistdata.bookings]);

    // Memoize locations to prevent unnecessary re-renders
    const processedLocations = useMemo(() => {
        if (!usersdata.drivers || !filteredBookings) return [];
        const liveBookings = filteredBookings.filter(bkg => bkg.status === 'STARTED');
        const drivers = usersdata.drivers;
        let locs = [];
        for (let i = 0; i < drivers.length; i++) {
            if (drivers[i].location) {
                let carImage = FALLBACK_CAR_ICON;
                let bookingRef = null;
                if (cars && cars.length > 0) {
                    for (let j = 0; j < cars.length; j++) {
                        if (cars[j].name === drivers[i].carType) {
                            carImage = cars[j].image;
                        }
                    }
                }
                for (let j = 0; j < liveBookings.length; j++) {
                    if (liveBookings[j].driver === drivers[i].uid) {
                        bookingRef = liveBookings[j].reference;
                    }
                }
                locs.push({
                    id: `driver-${drivers[i].uid}`,
                    lat: drivers[i].location.lat,
                    lng: drivers[i].location.lng,
                    drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                    carnumber: drivers[i].vehicleNumber,
                    cartype: drivers[i].carType,
                    carImage: carImage,
                    bookingRef: bookingRef,
                    fleetadmin: drivers[i].fleetadmin
                });
            }
        }
        if (profile.usertype === 'fleetadmin') {
            locs = locs.filter(driver => driver.fleetadmin === profile.uid);
        }
        return locs;
    }, [usersdata.drivers, profile.usertype, profile.uid, cars, filteredBookings]);

    useEffect(() => {
        setLocations(processedLocations);
    }, [processedLocations]);
    const selectedMarker = selectedMarkerId ? locations.find((m) => m.id === selectedMarkerId) : null;

    // Gross earnings: today and this month are Addis Ababa days and months.
    const [gross, setGross] = useState({ today: 0, month: 0, total: 0 });
    useEffect(() => {
        if (filteredBookings && filteredBookings.length > 0) {
            const nowMs = Date.now();
            const todayNo = addisDayNo(nowMs);
            const monthNo = addisMonthNo(nowMs);
            let todayTotal = 0;
            let monthlyTotal = 0;
            let total = 0;
            // A fleet admin earns their own fleet's commission; every other role
            // sums the company's convenience fees over the bookings it has loaded
            // (only relevant once settings.dashboardGrossEarnings = 'all').
            const isFleetAdmin = profile.usertype === 'fleetadmin';
            for (let i = 0; i < filteredBookings.length; i++) {
                const booking = filteredBookings[i];
                if ((booking.status === 'PAID' || booking.status === 'COMPLETE') && (!isFleetAdmin || booking.fleetadmin === profile.uid)) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = booking;
                    const ms = toMs(tripdate);
                    const isToday = addisDayNo(ms) === todayNo;
                    const isThisMonth = addisMonthNo(ms) === monthNo;
                    let amount = null;
                    if (!isFleetAdmin && convenience_fees && parseFloat(convenience_fees) > 0) {
                        amount = parseFloat(convenience_fees) - (parseFloat(discount) || 0);
                    }
                    if (isFleetAdmin && fleetCommission && parseFloat(fleetCommission) > 0) {
                        amount = parseFloat(fleetCommission);
                    }
                    if (amount !== null) {
                        if (isToday) todayTotal += amount;
                        if (isThisMonth) monthlyTotal += amount;
                        total += amount;
                    }
                }
            }
            setGross({ today: todayTotal, month: monthlyTotal, total });
        }
    }, [filteredBookings, profile.uid, profile.usertype]);

    const decimals = Number.isFinite(Number(settings.decimal)) ? Math.min(4, Math.max(0, Math.round(Number(settings.decimal)))) : 0;
    const fmtMoney = (amount) => {
        const v = Number(amount);
        const text = (Number.isFinite(v) ? v : 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        const symbol = settings.symbol || '';
        return (settings.swipe_symbol === false ? `${symbol} ${text}` : `${text} ${symbol}`).trim();
    };

    // Driver, customer, compliance and invitation counts from the user list.
    const counts = useMemo(() => {
        const result = {
            driverCount: 0,
            customerCount: 0,
            activeDrivers: 0,
            noCars: 0,
            noLicense: 0,
            noTIN: 0,
            driversWithInvites: 0,
            customersWithInvites: 0,
            totalWithInvites: 0,
            invitationPercentage: 0,
        };
        if (!usersdata.users) return result;
        const users = usersdata.users;
        for (let i = 0; i < users.length; i++) {
            const user = users[i];
            const isAuthorized = (profile.usertype === 'fleetadmin' && user.fleetadmin === profile.uid) ||
                profile.usertype === 'admin' ||
                profile.usertype === 'finance' ||
                profile.usertype === 'callcentersupervisor' ||
                profile.usertype === 'callcenteragent';
            if (user.usertype === 'driver' && isAuthorized) {
                result.driverCount++;
                if (user.driverActiveStatus === true) result.activeDrivers++;
                if (!user.vehicleNumber || !user.carType || !user.vehicleMake || !user.vehicleModel) result.noCars++;
                if (!user.licenseImage || !user.licenseImageBack) result.noLicense++;
                if (!user.tin) result.noTIN++;
                if (user.hasInvitedContacts === true) result.driversWithInvites++;
            }
            if (user.usertype === 'customer' && isAuthorized) {
                result.customerCount++;
                if (user.hasInvitedContacts === true) result.customersWithInvites++;
            }
        }
        // Customers aren't in the array for the paginated (call-center)
        // path — take their counts from the server-maintained
        // /customer_index_stats node so the tiles stay correct.
        if (usePaginatedCustomers && customerStats) {
            result.customerCount = typeof customerStats.total === 'number' ? customerStats.total : 0;
            result.customersWithInvites = typeof customerStats.withInvited === 'number' ? customerStats.withInvited : 0;
        }
        result.totalWithInvites = result.driversWithInvites + result.customersWithInvites;
        const totalUsers = result.driverCount + result.customerCount;
        result.invitationPercentage = totalUsers > 0 ? Math.round((result.totalWithInvites / totalUsers) * 100) : 0;
        return result;
    }, [usersdata.users, profile.usertype, profile.uid, usePaginatedCustomers, customerStats]);

    // Complaint metrics
    const complaintCounts = useMemo(() => {
        const list = complaindata && complaindata.list ? complaindata.list : null;
        if (!list) return { total: 0, processed: 0, pending: 0 };
        const processed = list.filter(item => item.check === true).length;
        return { total: list.length, processed, pending: list.length - processed };
    }, [complaindata]);

    // SOS metrics
    const sosCounts = useMemo(() => {
        const list = sosdata && sosdata.sos ? sosdata.sos : null;
        if (!list) return { total: 0, processed: 0, pending: 0 };
        const processed = list.filter(s => s.processed === true).length;
        return { total: list.length, processed, pending: list.length - processed };
    }, [sosdata]);

    // Booking chart: one pass over the loaded bookings into Addis buckets.
    // Only terminal/decision-point states plus two overlays are tracked:
    // intermediate statuses (NEW, ACCEPTED, ARRIVED, STARTED, REACHED, PAID,
    // PENDING) are transient. Fixed price (offerFare > 0: the customer set the
    // fare) and shared trips are counted independently of status, so a
    // booking can be complete AND a shared trip.
    const bookingChart = useMemo(() => {
        const buckets = bookingBuckets(bookingsTimePeriod, Date.now());
        const n = buckets.labels.length;
        const data = BOOKING_SERIES.map(() => new Array(n).fill(0));
        const forChart = (filteredBookings || []).filter(
            booking => selectedBookingTypes.includes(getBookingType(booking))
        );
        forChart.forEach((booking) => {
            const i = buckets.index(toMs(booking.tripdate));
            if (i < 0) return;
            BOOKING_SERIES.forEach((s, k) => {
                if (s.key === 'PRICE_OVERRIDE') {
                    if (booking.offerFare && parseFloat(booking.offerFare) > 0) data[k][i]++;
                } else if (s.key === 'SHARED_TRIP') {
                    if (booking.bookingType === 'shared_trip') data[k][i]++;
                } else if (booking.status === s.key) {
                    data[k][i]++;
                }
            });
        });
        const names = {
            COMPLETE: t('completed', { defaultValue: 'Completed' }),
            PAYMENT_PENDING: t('dash_series_payment_pending', { defaultValue: 'Payment pending' }),
            CANCELLED: t('cancelled', { defaultValue: 'Cancelled' }),
            SHARED_TRIP: t('dash_series_shared_trip', { defaultValue: 'Shared trip' }),
            PRICE_OVERRIDE: t('fixed_price', { defaultValue: 'Fixed price' }),
        };
        const series = BOOKING_SERIES.map((s, k) => ({ name: names[s.key], data: data[k], color: s.color, total: sumOf(data[k]) }));
        return { labels: buckets.labels, fullLabels: buckets.fullLabels, series, total: sumOf(series.map((s) => s.total)) };
    }, [filteredBookings, bookingsTimePeriod, selectedBookingTypes, t]);

    const bookingChartSeries = useMemo(() => bookingChart.series.map((s) => ({ name: s.name, data: s.data })), [bookingChart]);
    const bookingChartOptions = useMemo(() => ({
        chart: chartBase('area'),
        colors: bookingChart.series.map((s) => s.color),
        stroke: { curve: 'straight', width: 2 },
        fill: { type: 'solid', opacity: 0.1 },
        dataLabels: { enabled: false },
        markers: { size: 0, strokeColors: c.WHITE, strokeWidth: 2, hover: { size: 5 } },
        grid: { borderColor: VIZ.grid, strokeDashArray: 0, xaxis: { lines: { show: false } } },
        xaxis: {
            categories: axisCategories(bookingChart.labels, isPhone),
            labels: { style: axisText, rotate: -45, hideOverlappingLabels: true },
            axisBorder: { color: VIZ.grid },
            axisTicks: { show: false },
            tooltip: { enabled: false },
        },
        yaxis: { min: 0, forceNiceScale: true, tickAmount: yTickAmount(bookingChart.series), labels: { style: axisText, formatter: (v) => fmtCount(v) } },
        legend: chartLegend(bookingChart.series.map((s) => s.total)),
        tooltip: {
            shared: true,
            intersect: false,
            style: { fontFamily: FONT_STACK },
            x: { formatter: (val, opts) => (opts && bookingChart.fullLabels[opts.dataPointIndex]) || val },
        },
    }), [bookingChart, isPhone]);

    // Onboarding trend with compliance lines.
    const onboarding = useMemo(() => {
        if (!usersdata.users) return null;
        const users = usersdata.users;
        const drivers = users.filter(user => user.usertype === 'driver');
        const agents = users.filter(user =>
            user.usertype === 'fleetadmin' ||
            user.usertype === 'callcenteragent' ||
            user.usertype === 'callcentersupervisor' ||
            user.usertype === 'scout'
        );
        const buckets = onboardingBuckets(timePeriod, Date.now());
        const n = buckets.labels.length;
        const zeros = () => new Array(n).fill(0);

        // Agents see only their own onboarding; admin and call-centre
        // supervisor see every agent, scouts included.
        let relevantAgents = agents;
        if (profile.usertype === 'fleetadmin' || profile.usertype === 'callcenteragent' || profile.usertype === 'scout') {
            relevantAgents = agents.filter(agent => agent.id === profile.uid);
        }
        const isOrgWideView = profile.usertype === 'admin' || profile.usertype === 'callcentersupervisor';
        const agentMap = {};
        relevantAgents.forEach((agent) => {
            agentMap[agent.id] = { name: `${agent.firstName} ${agent.lastName}`, counts: zeros() };
        });
        const total = zeros();
        const noCar = zeros();
        const noLicence = zeros();
        const noTin = zeros();
        // Drivers onboarded without an agent (no fleetadmin set) — e.g.
        // self-signups, referral signups, or imports. Tracked as its own
        // bucket so admins can spot how many drivers are joining outside
        // the agent-driven onboarding funnel. Org-wide view only.
        const noAgent = zeros();
        drivers.forEach((driver) => {
            if (!driver.createdAt) return;
            const hasAgent = !!(driver.fleetadmin && agentMap[driver.fleetadmin]);
            if (!hasAgent && !isOrgWideView) return;
            const i = buckets.index(toMs(driver.createdAt));
            if (i < 0) return;
            total[i]++;
            if (hasAgent) agentMap[driver.fleetadmin].counts[i]++;
            else noAgent[i]++;
            if (!driver.vehicleNumber || !driver.carType || !driver.vehicleMake || !driver.vehicleModel) noCar[i]++;
            if (!driver.licenseImage || !driver.licenseImageBack) noLicence[i]++;
            if (!driver.tin) noTin[i]++;
        });

        const fixed = [];
        if (sumOf(total) > 0) {
            fixed.push({ id: 'total', name: t('total_onboarded', { defaultValue: 'Total onboarded' }), data: total });
            fixed.push({ id: 'no_car', name: t('no_car_info', { defaultValue: 'No car info' }), data: noCar });
            fixed.push({ id: 'no_licence', name: t('no_license_info', { defaultValue: 'No licence info' }), data: noLicence });
            fixed.push({ id: 'no_tin', name: t('no_tin_info', { defaultValue: 'No TIN info' }), data: noTin });
            if (isOrgWideView) {
                fixed.push({ id: 'no_agent', name: t('no_agent', { defaultValue: 'No agent' }), data: noAgent });
            }
        }
        const agentSeries = [];
        Object.keys(agentMap).forEach((uid) => {
            const agent = agentMap[uid];
            if (sumOf(agent.counts) > 0) {
                agentSeries.push({ id: `agent:${uid}`, name: agent.name, data: agent.counts });
            }
        });
        return { labels: buckets.labels, fullLabels: buckets.fullLabels, fixed, agents: agentSeries };
    }, [usersdata.users, profile.usertype, profile.uid, timePeriod, t]);

    const onboardingVisible = useMemo(() => {
        if (!onboarding) return [];
        const picked = onboarding.agents.filter((s) => selectedAgents.includes(s.id));
        return onboarding.fixed.concat(picked);
    }, [onboarding, selectedAgents]);

    const onboardingOptions = useMemo(() => {
        const labels = onboarding ? onboarding.labels : [];
        const fullLabels = onboarding ? onboarding.fullLabels : [];
        const colorOf = (s) => ONBOARDING_COLORS[s.id] || agentColorRef.current[s.id] || VIZ.fold;
        return {
            chart: chartBase('line'),
            colors: onboardingVisible.map(colorOf),
            stroke: {
                curve: 'straight',
                width: onboardingVisible.map((s) => (s.id === 'total' ? 3 : 2)),
                dashArray: onboardingVisible.map((s) => (isIssueSeries(s) ? 5 : 0)),
            },
            dataLabels: { enabled: false },
            markers: { size: 0, strokeColors: c.WHITE, strokeWidth: 2, hover: { size: 5 } },
            grid: { borderColor: VIZ.grid, strokeDashArray: 0, xaxis: { lines: { show: false } } },
            xaxis: {
                categories: axisCategories(labels, isPhone),
                labels: { style: axisText, rotate: -45, hideOverlappingLabels: true },
                axisBorder: { color: VIZ.grid },
                axisTicks: { show: false },
                tooltip: { enabled: false },
            },
            yaxis: {
                min: 0,
                forceNiceScale: true,
                tickAmount: yTickAmount(onboardingVisible),
                title: { text: t('driver_count', { defaultValue: 'Number of drivers' }), style: { fontFamily: FONT_STACK, color: c.TEXT_3, fontWeight: 500, fontSize: '12px' } },
                labels: { style: axisText, formatter: (v) => fmtCount(v) },
            },
            legend: chartLegend(onboardingVisible.map((s) => sumOf(s.data))),
            tooltip: {
                shared: true,
                intersect: false,
                style: { fontFamily: FONT_STACK },
                x: { formatter: (val, opts) => (opts && fullLabels[opts.dataPointIndex]) || val },
            },
        };
    }, [onboarding, onboardingVisible, isPhone, t]);
    const onboardingSeries = useMemo(() => onboardingVisible.map((s) => ({ name: s.name, data: s.data })), [onboardingVisible]);

    // Handler for time period change
    const handleTimePeriodChange = (event, newPeriod) => {
        if (newPeriod !== null) {
            setTimePeriod(newPeriod);
        }
    };

    // Handler for agent selection change
    const handleAgentSelectionChange = (event) => {
        const value = event.target.value;
        const next = typeof value === 'string' ? value.split(',') : value;
        const colors = agentColorRef.current;
        Object.keys(colors).forEach((id) => { if (!next.includes(id)) delete colors[id]; });
        next.forEach((id) => {
            if (colors[id]) return;
            const used = Object.keys(colors).map((k) => colors[k]);
            const free = AGENT_COLORS.find((col) => used.indexOf(col) < 0);
            colors[id] = free || VIZ.fold;
        });
        setSelectedAgents(next);
    };

    // Handler for booking-type filter change (multi-select)
    const handleBookingTypeFilterChange = (event) => {
        const value = event.target.value;
        setSelectedBookingTypes(typeof value === 'string' ? value.split(',') : value);
    };

    // Loading flags: each tile shows a skeleton until its own data is in,
    // instead of one spinner hiding the whole page (M11).
    const usersLoading = !usersdata.users && !(usersdata.error && usersdata.error.flag);
    const bookingsLoading = !bookinglistdata.bookings && filteredBookings.length === 0 && !(bookinglistdata.error && bookinglistdata.error.flag);
    const customersLoading = usePaginatedCustomers
        ? !customerStats && !(usersdata.customers && usersdata.customers.error)
        : usersLoading;
    const complaintsLoading = Boolean(complaindata && complaindata.loading) && !(complaindata && complaindata.list);
    const sosLoading = Boolean(sosdata && sosdata.loading) && !(sosdata && sosdata.sos);

    const showTiles = role === 'admin' || role === 'fleetadmin' || role === 'finance' || role === 'callcentersupervisor' || role === 'callcenteragent';
    const showInvitations = role === 'admin' || role === 'callcentersupervisor';
    const sosPending = sosCounts.pending > 0;
    const periodLabel = t('time_period', { defaultValue: 'Time period' });
    const chartSkeleton = <Skeleton variant="rounded" height={CHART_HEIGHT} />;
    const sectionTitleSx = { mr: 'auto', minWidth: 0, overflowWrap: 'anywhere' };

    return (
        <Box dir={isRTL ? 'rtl' : undefined} sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 }, minWidth: 0 }}>
            {/* Each card's icon has the colour of its kind (owner, 28 Oct: tell
                them apart, as Apple's Settings does): green money and active
                drivers, blue customers, teal drivers, amber compliance, violet
                complaints, red SOS, teal invitations. Only pending SOS is
                orange (its highlight), so it still stands out. */}
            {showGross ? (
                <Box component="section" aria-labelledby="dash-gross-title" sx={{ minWidth: 0 }}>
                    <Typography id="dash-gross-title" component="h2" variant="h6" sx={{ mb: 1.5 }}>
                        {t('gross_earning')}
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
                        <DashboardCard raised hue="success" icon={<TodayOutlinedIcon />} title={t('today_text')} value={fmtMoney(gross.today)} loading={bookingsLoading} />
                        <DashboardCard raised hue="success" icon={<CalendarMonthOutlinedIcon />} title={t('this_month_text')} value={fmtMoney(gross.month)} loading={bookingsLoading} />
                        <DashboardCard raised hue="success" icon={<PaymentsOutlinedIcon />} title={t('total')} value={fmtMoney(gross.total)} loading={bookingsLoading} />
                    </Box>
                </Box>
            ) : null}

            {/* Side by side (lg) the map card stretches to the tiles column, so
                its bottom lines up with the last tile (owner, 28 Oct: "the map
                height ... needs to align with Contact Invitations"). */}
            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    alignItems: { xs: 'start', lg: showTiles ? 'stretch' : 'start' },
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: showTiles ? 'minmax(0, 3fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' },
                }}
            >
                <Paper component="section" aria-labelledby="dash-map-title" sx={{ ...RAISED_SURFACE_SX, overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 1, px: 2, py: 1.5 }}>
                        <Typography id="dash-map-title" component="h2" variant="h6" sx={sectionTitleSx}>
                            {t('real_time_driver_section_text')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: c.TEXT_3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {t('zoom_level', { defaultValue: 'Zoom level' })}: {mapZoom}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            color="neutral"
                            startIcon={<MyLocationRoundedIcon />}
                            onClick={centreOnMe}
                            disabled={!mapLoaded || locating}
                        >
                            {locating ? t('dash_locating', { defaultValue: 'Finding you…' }) : t('dash_centre_on_me', { defaultValue: 'Centre on me' })}
                        </Button>
                        <Button
                            size="small"
                            variant="text"
                            color="neutral"
                            startIcon={<RestartAltRoundedIcon />}
                            onClick={resetView}
                            disabled={!mapLoaded}
                        >
                            {t('reset_view', { defaultValue: 'Reset view' })}
                        </Button>
                        {demandAvailable ? (
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={<Switch checked={demandOn} onChange={(e) => toggleDemand(e.target.checked)} />}
                                label={t('dash_demand_toggle', { defaultValue: 'Hot zones' })}
                            />
                        ) : null}
                    </Box>
                    {locationNote ? (
                        <InlineMessage kind="info" onClose={() => setLocationNote('')} sx={{ mx: 2, mb: 1.5 }}>
                            {locationNote}
                        </InlineMessage>
                    ) : null}
                    {/* The map and the Hot zones overlay share one positioned box so
                        the floating card is offset from the MAP, not from a header
                        whose height changes when the title wraps. */}
                    <Box
                        sx={{
                            position: 'relative',
                            bgcolor: c.SURFACE_ALT,
                            height: showTiles ? { xs: MAP_HEIGHT, lg: 'auto' } : MAP_HEIGHT,
                            ...(showTiles ? { flex: { lg: '1 1 auto' }, minHeight: { lg: MAP_MIN_HEIGHT } } : {}),
                        }}
                    >
                        {mapsState === 'ready' ? (
                            <GoogleMap
                                mapContainerStyle={MAP_CONTAINER_STYLE}
                                center={DEFAULT_CENTER}
                                zoom={mapZoom}
                                options={MAP_OPTIONS}
                                onLoad={handleMapLoad}
                                onUnmount={handleMapUnmount}
                                onZoomChanged={handleZoomChange}
                            >
                                {locations.map((marker) => (
                                    <Marker
                                        key={marker.id}
                                        position={{ lat: marker.lat, lng: marker.lng }}
                                        title={marker.drivername}
                                        icon={{
                                            url: marker.carImage,
                                            scaledSize: new window.google.maps.Size(currentIconSize.width, currentIconSize.height),
                                            origin: new window.google.maps.Point(0, 0),
                                            anchor: new window.google.maps.Point(currentIconSize.width / 2, currentIconSize.height / 2)
                                        }}
                                        onClick={() => setSelectedMarkerId(marker.id)}
                                    />
                                ))}
                                {selectedMarker ? (
                                    <InfoWindow
                                        position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                                        onCloseClick={() => setSelectedMarkerId(null)}
                                    >
                                        <Box sx={{ px: 0.5, py: 0.25, fontFamily: FONT_STACK, fontSize: 13, lineHeight: 1.45, color: c.INK, whiteSpace: 'nowrap' }}>
                                            <Box sx={{ fontWeight: 700 }}>{selectedMarker.drivername}</Box>
                                            <Box sx={{ color: c.INK_2 }}>{selectedMarker.carnumber}</Box>
                                        </Box>
                                    </InfoWindow>
                                ) : null}
                                {/* Hot zones rank pins (top 3 of the selected tab). Plain Markers
                                    with an inline SVG icon: no map ID or AdvancedMarker needed. */}
                                {showDemand && demandView.zones.map((z, i) => (
                                    <Marker
                                        key={`hz-${demandTab}-${i}`}
                                        position={{ lat: z.lat, lng: z.lng }}
                                        icon={{
                                            url: zonePinIcon(i + 1),
                                            scaledSize: new window.google.maps.Size(36, 44),
                                            anchor: new window.google.maps.Point(18, 44),
                                        }}
                                        title={[
                                            z.en || z.am || t('dash_demand_zone_n', { n: i + 1, defaultValue: 'Zone {{n}}' }),
                                            z.drivers ? t(z.drivers === 1 ? 'dash_demand_driver_one' : 'dash_demand_drivers', { n: z.drivers, defaultValue: z.drivers === 1 ? '{{n}} driver there' : '{{n}} drivers there' }) : '',
                                        ].filter(Boolean).join(' · ')}
                                        zIndex={1000 + (3 - i)}
                                    />
                                ))}
                            </GoogleMap>
                        ) : null}
                        {mapsState === 'waiting' ? (
                            <Box role="status" aria-label={t('dash_map_loading', { defaultValue: 'Loading map…' })} sx={{ position: 'absolute', inset: 0 }}>
                                <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%', transform: 'none' }} />
                            </Box>
                        ) : null}
                        {mapsState === 'failed' ? (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <EmptyState
                                    compact
                                    icon={<MapOutlinedIcon />}
                                    title={t('dash_map_failed', { defaultValue: 'The map did not load. Check the connection, then reload the page.' })}
                                    actionLabel={t('dash_reload_page', { defaultValue: 'Reload page' })}
                                    onAction={() => window.location.reload()}
                                />
                            </Box>
                        ) : null}
                        {showDemand ? (
                            <>
                                <DemandHeatLayer map={mapRef.current} cells={demandView.cells} visible />
                                <DemandZonesCard
                                    status={demand.status}
                                    snap={demand.snap}
                                    meta={demand.meta}
                                    zones={demandView.zones}
                                    quiet={demandView.quiet}
                                    tab={demandTab}
                                    onTab={setDemandTab}
                                    onRefresh={demand.refresh}
                                    busy={demand.busy}
                                    manualAt={demand.manualAt}
                                    fetchedAt={demand.fetchedAt}
                                    isAdmin={role === 'admin'}
                                    style={isPhone ? { left: 8, right: 8, width: 'auto' } : undefined}
                                />
                            </>
                        ) : null}
                    </Box>
                </Paper>

                {showTiles ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 1.5,
                            alignContent: 'start',
                            minWidth: 0,
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                sm: 'repeat(3, minmax(0, 1fr))',
                                md: 'repeat(4, minmax(0, 1fr))',
                                lg: 'minmax(0, 1fr)',
                            },
                        }}
                    >
                        <DashboardCard raised
                            icon={<PeopleAltOutlinedIcon />}
                            hue="info"
                            title={t('total_customers', { defaultValue: 'Total customers' })}
                            value={fmtCount(counts.customerCount)}
                            loading={customersLoading}
                        />
                        <DashboardCard raised
                            icon={<DirectionsCarOutlinedIcon />}
                            hue="teal"
                            title={t('total_drivers', { defaultValue: 'Total drivers' })}
                            value={fmtCount(counts.driverCount)}
                            loading={usersLoading}
                        />
                        <DashboardCard raised
                            icon={<SensorsRoundedIcon />}
                            hue="success"
                            title={t('active_driver', { defaultValue: 'Active drivers' })}
                            value={fmtCount(counts.activeDrivers)}
                            loading={usersLoading}
                        />
                        <DashboardCard raised
                            icon={<AssignmentLateOutlinedIcon />}
                            hue="amber"
                            title={t('compliance_issues', { defaultValue: 'Compliance issues' })}
                            loading={usersLoading}
                            stats={[
                                { label: t('dash_no_car', { defaultValue: 'No car' }), value: fmtCount(counts.noCars) },
                                { label: t('dash_no_licence', { defaultValue: 'No licence' }), value: fmtCount(counts.noLicense) },
                                { label: t('dash_no_tin', { defaultValue: 'No TIN' }), value: fmtCount(counts.noTIN) },
                            ]}
                        />
                        <DashboardCard raised
                            icon={<ChatBubbleOutlineRoundedIcon />}
                            hue="violet"
                            title={t('complaints_metrics', { defaultValue: 'Complaints' })}
                            loading={complaintsLoading}
                            stats={[
                                { label: t('total'), value: fmtCount(complaintCounts.total) },
                                { label: t('processed'), value: fmtCount(complaintCounts.processed) },
                                { label: t('pending'), value: fmtCount(complaintCounts.pending) },
                            ]}
                        />
                        {/* Orange is kept for the one figure to act on: pending SOS. */}
                        <DashboardCard raised
                            icon={<WarningAmberRoundedIcon />}
                            hue="danger"
                            title={t('sos_metrics', { defaultValue: 'SOS reports' })}
                            loading={sosLoading}
                            highlight={sosPending}
                            stats={[
                                { label: t('total'), value: fmtCount(sosCounts.total) },
                                { label: t('processed'), value: fmtCount(sosCounts.processed) },
                                { label: t('pending'), value: fmtCount(sosCounts.pending), highlight: sosPending },
                            ]}
                        />
                        {/* Contact invitations: admin and call-centre supervisor only */}
                        {showInvitations ? (
                            <DashboardCard raised
                                icon={<GroupAddOutlinedIcon />}
                            hue="teal"
                                title={t('contact_invitations', { defaultValue: 'Contact invitations' })}
                                value={`${counts.invitationPercentage}%`}
                                loading={usersLoading}
                                stats={[
                                    { label: t('drivers'), value: fmtCount(counts.driversWithInvites), of: fmtCount(counts.driverCount) },
                                    { label: t('customers'), value: fmtCount(counts.customersWithInvites), of: fmtCount(counts.customerCount) },
                                    { label: t('total'), value: fmtCount(counts.totalWithInvites), of: fmtCount(counts.driverCount + counts.customerCount) },
                                ]}
                            />
                        ) : null}
                    </Box>
                ) : null}
            </Box>

            <Paper component="section" aria-labelledby="dash-bookings-title" sx={{ ...RAISED_SURFACE_SX, p: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Typography id="dash-bookings-title" component="h2" variant="h6" sx={sectionTitleSx}>
                        {t('booking_chart')}
                    </Typography>
                    {/* Booking-source filter — narrows the chart by where bookings came from */}
                    <FormControl size="small" sx={{ minWidth: 190, maxWidth: '100%' }}>
                        <Select
                            multiple
                            displayEmpty
                            value={selectedBookingTypes}
                            onChange={handleBookingTypeFilterChange}
                            input={<OutlinedInput />}
                            inputProps={{ 'aria-label': t('booking_type_filter', { defaultValue: 'Booking type' }) }}
                            renderValue={(selected) =>
                                selected.length === 0
                                    ? t('booking_type_filter', { defaultValue: 'Booking type' })
                                    : selected.length === BOOKING_TYPE_ORDER.length
                                        ? t('all_booking_types', { defaultValue: 'All types' })
                                        : `${selected.length} ${t('selected', { defaultValue: 'selected' })}`
                            }
                            MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                        >
                            {BOOKING_TYPE_ORDER.map((typeKey) => {
                                const meta = bookingTypeMeta[typeKey];
                                return (
                                    <MenuItem key={typeKey} value={typeKey}>
                                        <Checkbox
                                            checked={selectedBookingTypes.indexOf(typeKey) > -1}
                                            sx={{ color: meta.color, '&.Mui-checked': { color: meta.color } }}
                                        />
                                        <Box component="span" aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: meta.color, display: 'inline-block', mr: 1, flex: 'none' }} />
                                        <ListItemText primary={bookingTypeLabelFor(typeKey, t)} />
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <ToggleButtonGroup
                        value={bookingsTimePeriod}
                        exclusive
                        color="primary"
                        size="small"
                        aria-label={periodLabel}
                        onChange={(event, newPeriod) => {
                            if (newPeriod !== null) {
                                setBookingsTimePeriod(newPeriod);
                            }
                        }}
                    >
                        <ToggleButton value="day">{t('dash_period_day', { defaultValue: 'Day' })}</ToggleButton>
                        <ToggleButton value="week">{t('dash_period_week', { defaultValue: 'Week' })}</ToggleButton>
                        <ToggleButton value="month">{t('dash_period_month', { defaultValue: 'Month' })}</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {bookingsLoading ? chartSkeleton : null}
                {!bookingsLoading && selectedBookingTypes.length === 0 ? (
                    <EmptyState
                        compact
                        icon={<InsightsOutlinedIcon />}
                        title={t('dash_pick_booking_type', { defaultValue: 'Pick at least one booking type to see the chart.' })}
                        actionLabel={t('dash_show_all_types', { defaultValue: 'Show all types' })}
                        onAction={() => setSelectedBookingTypes(BOOKING_TYPE_ORDER)}
                    />
                ) : null}
                {!bookingsLoading && selectedBookingTypes.length > 0 && bookingChart.total === 0 ? (
                    <EmptyState
                        compact
                        icon={<InsightsOutlinedIcon />}
                        title={t('dash_no_bookings_period', { defaultValue: 'No bookings in this period.' })}
                    />
                ) : null}
                {!bookingsLoading && bookingChart.total > 0 ? (
                    <Suspense fallback={chartSkeleton}>
                        <Chart
                            options={bookingChartOptions}
                            series={bookingChartSeries}
                            type="area"
                            width="100%"
                            height={CHART_HEIGHT}
                        />
                    </Suspense>
                ) : null}
            </Paper>

            {/* Onboarding chart: the same roles as the tiles */}
            {showTiles ? (
                <Paper component="section" aria-labelledby="dash-onboarding-title" sx={{ ...RAISED_SURFACE_SX, p: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Typography id="dash-onboarding-title" component="h2" variant="h6" sx={sectionTitleSx}>
                            {t('onboarding_compliance_trend', { defaultValue: 'Driver onboarding and compliance' })}
                        </Typography>
                        <ToggleButtonGroup
                            value={timePeriod}
                            exclusive
                            color="primary"
                            size="small"
                            aria-label={periodLabel}
                            onChange={handleTimePeriodChange}
                        >
                            <ToggleButton value="day">{t('last_30_days', { defaultValue: 'Last 30 days' })}</ToggleButton>
                            <ToggleButton value="week">{t('dash_period_week', { defaultValue: 'Week' })}</ToggleButton>
                            <ToggleButton value="month">{t('dash_period_month', { defaultValue: 'Month' })}</ToggleButton>
                        </ToggleButtonGroup>
                        {/* Agent picker: compare agents with the totals */}
                        {onboarding && onboarding.agents.length > 0 ? (
                            <FormControl size="small" sx={{ minWidth: 200, maxWidth: '100%' }}>
                                <Select
                                    multiple
                                    displayEmpty
                                    value={selectedAgents}
                                    onChange={handleAgentSelectionChange}
                                    input={<OutlinedInput />}
                                    inputProps={{ 'aria-label': t('dash_compare_agents', { defaultValue: 'Compare agents' }) }}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) return t('dash_compare_agents', { defaultValue: 'Compare agents' });
                                        if (selected.length === 1) {
                                            const one = onboarding.agents.find((s) => s.id === selected[0]);
                                            return one ? one.name : `1 ${t('selected', { defaultValue: 'selected' })}`;
                                        }
                                        return `${selected.length} ${t('selected', { defaultValue: 'selected' })}`;
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                                >
                                    {onboarding.agents.map((series) => (
                                        <MenuItem key={series.id} value={series.id}>
                                            <Checkbox checked={selectedAgents.indexOf(series.id) > -1} />
                                            <ListItemText primary={series.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : null}
                    </Box>
                    {usersLoading ? chartSkeleton : null}
                    {!usersLoading && onboardingVisible.length === 0 ? (
                        <EmptyState
                            compact
                            icon={<InsightsOutlinedIcon />}
                            title={t('dash_no_onboarded', { defaultValue: 'No drivers joined in this period.' })}
                        />
                    ) : null}
                    {!usersLoading && onboardingVisible.length > 0 ? (
                        <Suspense fallback={chartSkeleton}>
                            <Chart
                                options={onboardingOptions}
                                series={onboardingSeries}
                                type="line"
                                width="100%"
                                height={CHART_HEIGHT}
                            />
                        </Suspense>
                    ) : null}
                </Paper>
            ) : null}

            {/* Fleet & app health — nightly server snapshot (/dashboard_stats,
                written by the reconcile jobs), ONE small read per page
                load; only the online count is live. */}
            {showFleetHealth ? (
                <Box component="section" sx={{ minWidth: 0 }}>
                    <Suspense fallback={<Skeleton variant="rounded" height={320} />}>
                        <FleetHealthCharts
                            stats={fleetStats}
                            liveOnline={counts.activeDrivers}
                            loading={fleetStatsLoading}
                            error={fleetStatsError}
                            canBuild={role === 'admin'}
                            building={fleetStatsBuilding}
                            onBuild={buildFleetStats}
                        />
                    </Suspense>
                </Box>
            ) : null}
        </Box>
    );
};

export default Dashboard;
